import { useEffect, useState } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { useParams } from "react-router-dom";

import { ClassroomWrapper } from "./classroom_wrapper";
import { FlightErrorBoundary } from "./error_boundary";
import { LoggingWrapper } from "./logging_wrapper";

import { ENVIRONMENT } from "config";
import { FlightAPI, FlightAPIContext } from "contexts/flight_api_context";
import { ForumAPI } from "contexts/forum_api_context";
import { StoreType, getClassroomReduxPubsubClient } from "pubsub/client";
import { PubsubStatsCollector } from "pubsub/pubsub_stats_collector";
import { setFlightMonitoringUrl } from "store/reducers/local";
import { decodeFlightAuthToken } from "utils/auth";
import { redirectToHome, redirectToNotFound } from "utils/classroom";
import { getAuthToken } from "utils/cookie";
import { Logger } from "utils/logger";

function App() {
  const { classId } = useParams();
  const [userId, setUserId] = useState<string | null>(null);
  const [store, setStore] = useState<StoreType>();
  const [flightAPIContextValue, setFlightAPIContextValue] = useState<FlightAPI | null>(null);

  if (classId && !window.flightLogger) {
    window.flightLogger = new Logger(classId);
    window.pubsubStatsCollector = new PubsubStatsCollector();
  }

  const flightAuthToken = getAuthToken();

  useEffect(() => {
    async function initializeReduxPubsub() {
      if (!classId || classId === "null") {
        window.flightLogger.warn("[App] Class ID not found, classId:", classId);
        redirectToNotFound();
        return;
      }

      const _userId = decodeFlightAuthToken(flightAuthToken);
      if (!_userId) {
        return;
      }
      setUserId(_userId);

      // Set up the Forum API client
      const forumAPI = new ForumAPI();
      const response = await forumAPI.get(`api/v1/classes/${classId}/get_flight_urls`);
      if (response.ok) {
        const { flight_url, flight_monitoring_url } = await response.json();

        if (!flight_url) {
          window.flightLogger.error("[App] Flight URL not found in response");
          redirectToNotFound();
          return;
        }

        // Make request to Flight API before initializing websocket connections to ensure
        // that the user's presence obtained from the Flight API is accurate.
        const flightAPI = new FlightAPI(flight_url, _userId);
        await flightAPI.start(classId);

        // Set up redux-pubsub store and subscribe it to the classroomSocket
        const reduxPubsubClient = getClassroomReduxPubsubClient(
          classId,
          flightAuthToken,
          _userId,
          flight_url,
          flight_monitoring_url,
        );

        window.flightLogger.log("[App] Initializing Redux store");
        reduxPubsubClient.store.dispatch(
          setFlightMonitoringUrl({ flightMonitoringUrl: flight_monitoring_url }),
        );
        setStore(reduxPubsubClient.store);
        setFlightAPIContextValue(flightAPI);
      } else {
        window.flightLogger.error(
          `[App] Failed to get flight url for classId=${classId} userId=${_userId}`,
        );
        if (response.status === 403) {
          window.flightLogger.error(`[App] User ${_userId} is not allowed to enter the classroom.`);
          redirectToHome();
        }
        if (response.status === 404) {
          window.flightLogger.error(`[App] Class ${classId} not found`);
          redirectToNotFound();
        }
      }
    }

    initializeReduxPubsub();
  }, [flightAuthToken, classId]);

  if (!classId || !store || !flightAPIContextValue || !userId) return null;

  if (!ENVIRONMENT.startsWith("production")) {
    window.flightStore = store;
  }

  return (
    <LoggingWrapper userId={userId} classId={classId || ""}>
      <FlightErrorBoundary includeTechSupport margin="8px">
        <ReduxProvider store={store}>
          <FlightAPIContext.Provider value={flightAPIContextValue}>
            <ClassroomWrapper userId={userId} />
          </FlightAPIContext.Provider>
        </ReduxProvider>
      </FlightErrorBoundary>
    </LoggingWrapper>
  );
}

export { App };
